import { convertMathMlEquationToLatex, dispatchEditEquationEvent } from '@common/prosemirror/commands/equation.command';
import { EquationToolbarPopupPluginViewComponent } from '@portal-core/text-editor/plugin-views/components/equation-toolbar-popup-plugin-view/equation-toolbar-popup-plugin-view.component';
import { ToolbarPopupPluginViewBase } from '@portal-core/text-editor/plugin-views/toolbar-popup.plugin-view.base';
import { isEditableEquationNode, isEquationInEditMode, isEquationNode, tryConvertMathMlToLatex } from '@portal-core/ui/equation/util/equation-node';
import { ElementOverlayService } from '@portal-core/ui/overlay/services/element.overlay.service';
import { EditorState, NodeSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Observable } from 'rxjs';

export interface EquationToolbarPopupPluginOptions {
  isReadonly$: Observable<boolean>;
}

export class EquationToolbarPopupPluginView extends ToolbarPopupPluginViewBase<EquationToolbarPopupPluginViewComponent> {
  private nodePos: number;

  constructor(elementOverlayService: ElementOverlayService, options: EquationToolbarPopupPluginOptions) {
    super(EquationToolbarPopupPluginViewComponent, elementOverlayService, options.isReadonly$);
  }

  updateComponent(editorView: EditorView, _prevEditorState: EditorState) {
    if (this.isReadonly) {
      this.component.closePopup();
      return;
    }

    const selection = editorView.state.selection;
    const isInEquation = selection instanceof NodeSelection && isEquationNode(selection.node);

    if (isInEquation) {
      // The equation toolbar popup and MathLive's own edit-mode UI shouldn't show at once.
      const equationDom = editorView.nodeDOM(selection.from) as HTMLElement | null;
      if (isEquationInEditMode(equationDom)) {
        this.component.closePopup();
        return;
      }

      this.nodePos = selection.from;
      // PBI #186777: MathML equations are read-only — the component disables Edit (rather than
      // dispatching a click that EquationNodeView.enterEditMode() would always block anyway, see
      // isEditableEquationNode()'s doc comment) and shows an explanatory tooltip instead. Delete is
      // unaffected; a non-editable equation can still be removed.
      this.component.canEdit = isEditableEquationNode(selection.node);

      const nodeRect = (editorView as any).lastSelectedViewDesc?.nodeDOM?.getBoundingClientRect();
      if (!nodeRect) return;

      const popupMarginTop = 2;
      const popupArrowHeight = 7;
      this.component.openPopup(nodeRect.left, nodeRect.bottom + popupMarginTop + popupArrowHeight);
    } else {
      this.component.closePopup();
    }
  }

  onComponentCreated() {
    super.onComponentCreated();
    this.subscriptions.add(this.component.editNode.subscribe(this.onEditNode.bind(this)));
    this.subscriptions.add(this.component.convertToLatex.subscribe(this.onConvertToLatex.bind(this)));
  }

  onEditNode() {
    dispatchEditEquationEvent(this.editorView, this.nodePos);
    this.component.closePopup();
  }

  onConvertToLatex() {
    const node = this.editorView.state.doc.nodeAt(this.nodePos);
    if (!node) {
      return;
    }

    const result = tryConvertMathMlToLatex(node);
    if (result.status === 'failed') {
      // Leave the equation untouched rather than replacing it with an empty one the user would
      // then have to undo.
      this.component.showFailedConversionError();
      return;
    }

    if (result.status === 'lossy') {
      this.component.showLossyConversionWarning();
    }

    convertMathMlEquationToLatex(this.nodePos, result.latex)(this.editorView.state, tr => this.editorView.dispatch(tr));
    this.component.closePopup();
  }
}
